import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, Users, Plus, Edit, Trash2, AlertCircle, CheckCircle, 
  User, CreditCard, Clock, Calendar, Award, ChevronRight, X, Trophy
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import CashfreeCheckout from '../components/CashfreeCheckout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IAPLDashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [formData, setFormData] = useState({
    team_name: '',
    project_idea: '',
    ai_focus_area: '',
    members: [
      { name: user?.name || '', email: user?.email || '', phone: user?.phone || '', college: '', year_of_study: '', skills: [] }
    ]
  });
  
  // Check if team has completed initial payment (can only add members, not edit details)
  const isPaymentCompleted = team?.payment_status === 'completed' || team?.payment_status === 'partial';

  useEffect(() => {
    // Wait for auth to initialize before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamRes, configRes] = await Promise.all([
        axios.get(`${API}/iapl/team/my-team`),
        axios.get(`${API}/iapl/config`)
      ]);
      setTeam(teamRes.data.team);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Failed to fetch IAPL data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setShowPaymentModal(false);
    toast.success('Payment successful! Your IAPL registration is confirmed.');
    fetchData(); // Refresh team data
  };

  const handlePaymentFailure = (error) => {
    setShowPaymentModal(false);
    toast.error(error?.error || 'Payment failed. Please try again.');
  };

  const addMember = () => {
    if (formData.members.length >= 5) {
      toast.error('Maximum 5 members allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, { name: '', email: '', phone: '', college: '', year_of_study: '', skills: [] }]
    }));
  };

  const removeMember = (index) => {
    if (formData.members.length <= 2) {
      toast.error('Minimum 2 members required');
      return;
    }
    if (index === 0) {
      toast.error('Cannot remove team leader');
      return;
    }
    setFormData(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };

  const updateMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    
    if (formData.members.length < 2) {
      toast.error('IAPL teams require at least 2 members');
      return;
    }

    // Validate each member's required fields
    const memberErrors = [];
    formData.members.forEach((member, index) => {
      if (!member.name?.trim()) {
        memberErrors.push(`Member ${index + 1}: Name is required`);
      }
      if (!member.email?.trim()) {
        memberErrors.push(`Member ${index + 1}: Email is required`);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
        memberErrors.push(`Member ${index + 1}: Please enter a valid email`);
      }
      if (!member.phone?.trim()) {
        memberErrors.push(`Member ${index + 1}: Phone is required`);
      }
    });

    if (memberErrors.length > 0) {
      const errorMessage = memberErrors.length > 3 
        ? `${memberErrors.slice(0, 3).join('. ')}. And ${memberErrors.length - 3} more errors.`
        : memberErrors.join('. ');
      toast.error(errorMessage);
      return;
    }

    // Check for duplicate emails
    const emails = formData.members.map(m => m.email.toLowerCase().trim());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      toast.error('Each team member must have a unique email address');
      return;
    }

    if (!formData.ai_focus_area) {
      toast.error('Please select an AI focus area');
      return;
    }

    if (formData.project_idea.length < 50) {
      toast.error('Project idea must be at least 50 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/iapl/team/create`, formData);
      toast.success('IAPL team created successfully!');
      setTeam(response.data.team);
      setShowCreateForm(false);
    } catch (err) {
      // Handle Pydantic validation errors
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorsByField = {};
        detail.forEach(e => {
          const loc = e.loc || [];
          const fieldParts = loc.filter(p => typeof p === 'string' && p !== 'body');
          const fieldName = fieldParts.length > 0 ? fieldParts[fieldParts.length - 1] : 'field';
          const msg = e.msg || 'Invalid value';
          if (!errorsByField[fieldName]) errorsByField[fieldName] = new Set();
          errorsByField[fieldName].add(msg);
        });
        const uniqueMessages = [...new Set(Object.entries(errorsByField).map(([field, msgs]) => {
          const formattedField = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
          return `${formattedField}: ${[...msgs].join(', ')}`;
        }))];
        toast.error(uniqueMessages.slice(0, 3).join('. '));
      } else {
        toast.error(detail || 'Failed to create team');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!window.confirm('Are you sure you want to delete your IAPL team? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/iapl/team/delete`);
      toast.success('Team deleted successfully');
      setTeam(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete team');
    }
  };
  
  // Open edit form with existing team data
  const handleEditTeam = () => {
    setFormData({
      team_name: team.team_name || '',
      project_idea: team.project_idea || '',
      ai_focus_area: team.ai_focus_area || '',
      members: team.members?.map(m => ({
        name: m.name || '',
        email: m.email || '',
        phone: m.phone || '',
        college: m.college || '',
        year_of_study: m.year_of_study || '',
        skills: m.skills || [],
        id: m.id,
        is_leader: m.is_leader,
        payment_status: m.payment_status
      })) || []
    });
    setShowEditForm(true);
  };
  
  // Handle team update
  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    
    if (formData.members.length < 2) {
      toast.error('IAPL teams require at least 2 members');
      return;
    }
    
    if (formData.members.length > 5) {
      toast.error('Maximum 5 members allowed');
      return;
    }

    // Validate each member's required fields
    const memberErrors = [];
    formData.members.forEach((member, index) => {
      if (!member.name?.trim()) {
        memberErrors.push(`Member ${index + 1}: Name is required`);
      }
      if (!member.email?.trim()) {
        memberErrors.push(`Member ${index + 1}: Email is required`);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
        memberErrors.push(`Member ${index + 1}: Please enter a valid email`);
      }
      if (!member.phone?.trim()) {
        memberErrors.push(`Member ${index + 1}: Phone is required`);
      }
    });

    if (memberErrors.length > 0) {
      const errorMessage = memberErrors.length > 3 
        ? `${memberErrors.slice(0, 3).join('. ')}. And ${memberErrors.length - 3} more errors.`
        : memberErrors.join('. ');
      toast.error(errorMessage);
      return;
    }

    // Check for duplicate emails
    const emails = formData.members.map(m => m.email.toLowerCase().trim());
    const duplicateEmails = emails.filter((email, index) => emails.indexOf(email) !== index);
    if (duplicateEmails.length > 0) {
      toast.error('Each team member must have a unique email address');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/iapl/team/update`, formData);
      toast.success('Team updated successfully!');
      setTeam(response.data.team);
      setShowEditForm(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorsByField = {};
        detail.forEach(e => {
          const loc = e.loc || [];
          const fieldParts = loc.filter(p => typeof p === 'string' && p !== 'body');
          const fieldName = fieldParts.length > 0 ? fieldParts[fieldParts.length - 1] : 'field';
          const msg = e.msg || 'Invalid value';
          if (!errorsByField[fieldName]) errorsByField[fieldName] = new Set();
          errorsByField[fieldName].add(msg);
        });
        const uniqueMessages = [...new Set(Object.entries(errorsByField).map(([field, msgs]) => {
          const formattedField = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
          return `${formattedField}: ${[...msgs].join(', ')}`;
        }))];
        toast.error(uniqueMessages.slice(0, 3).join('. '));
      } else {
        toast.error(detail || 'Failed to update team');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <div className="text-center">
          <Brain className="text-purple-400 mx-auto mb-4" size={64} />
          <h1 className="text-3xl font-bold mb-4">IAPL Registration Not Open</h1>
          <p className="text-gray-400 mb-8">The India's AI Premium League registration is not currently available.</p>
          <button
            onClick={() => navigate('/ai-league')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Learn More About IAPL
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border-b border-purple-500/30">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="text-purple-400" size={32} />
            <span className="text-xl font-bold text-purple-400">IAPL Dashboard</span>
          </div>
          <h1 className="text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">
            India's AI Premium League
          </h1>
          <p className="text-gray-400 mt-2">Manage your IAPL team and submissions</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* No Team State */}
        {!team && !showCreateForm && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-purple-600/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="text-purple-400" size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-4">Create Your IAPL Team</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Register your team for India's AI Premium League and compete for ₹{(config.prize_pool || 500000).toLocaleString()} prize pool!
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white px-8 py-3 rounded-lg font-medium inline-flex items-center gap-2 transition-all"
              data-testid="create-iapl-team-btn"
            >
              <Plus size={20} />
              Create Team
            </button>
          </div>
        )}

        {/* Create Team Form */}
        {showCreateForm && !team && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Create IAPL Team</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateTeam} className="space-y-6">
              {/* Team Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Team Name *</label>
                  <input
                    type="text"
                    value={formData.team_name}
                    onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Enter team name"
                    required
                    minLength={3}
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">AI Focus Area *</label>
                  <select
                    value={formData.ai_focus_area}
                    onChange={(e) => setFormData({ ...formData, ai_focus_area: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  >
                    <option value="">Select Focus Area</option>
                    {config.ai_focus_areas?.map((area, idx) => (
                      <option key={idx} value={area}>{area}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Idea * <span className="text-gray-500">({formData.project_idea.length}/2000)</span>
                </label>
                <textarea
                  value={formData.project_idea}
                  onChange={(e) => setFormData({ ...formData, project_idea: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Describe your AI project idea in detail (minimum 50 characters)"
                  rows={4}
                  required
                  minLength={50}
                  maxLength={2000}
                />
              </div>

              {/* Team Members */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium text-gray-300">
                    Team Members ({formData.members.length}/5) *
                  </label>
                  <button
                    type="button"
                    onClick={addMember}
                    disabled={formData.members.length >= 5}
                    className="text-purple-400 hover:text-purple-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    + Add Member
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.members.map((member, index) => (
                    <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-purple-400">
                          {index === 0 ? 'Team Leader' : `Member ${index + 1}`}
                        </span>
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => removeMember(index)}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => updateMember(index, 'name', e.target.value)}
                          className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="Full Name *"
                          required
                        />
                        <input
                          type="email"
                          value={member.email}
                          onChange={(e) => updateMember(index, 'email', e.target.value)}
                          className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="Email *"
                          required
                        />
                        <input
                          type="tel"
                          value={member.phone}
                          onChange={(e) => updateMember(index, 'phone', e.target.value)}
                          className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="Phone *"
                          required
                        />
                        <input
                          type="text"
                          value={member.college}
                          onChange={(e) => updateMember(index, 'college', e.target.value)}
                          className="px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="College/University *"
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
                {formData.members.length < 2 && (
                  <p className="text-yellow-400 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    IAPL teams require at least 2 members
                  </p>
                )}
              </div>

              {/* Pricing Summary */}
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Entry Fee ({formData.members.length} members × ₹{(config.entry_fee || 3999).toLocaleString()})</span>
                  <span className="text-2xl font-bold text-white">
                    ₹{((config.entry_fee || 3999) * formData.members.length).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || formData.members.length < 2}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="submit-iapl-team-btn"
                >
                  {submitting ? 'Creating...' : 'Create Team & Proceed to Payment'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Team Dashboard */}
        {team && !showEditForm && (
          <div className="space-y-6">
            {/* Team Overview Card */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-2xl font-bold">{team.team_name}</h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      team.payment_status === 'completed' 
                        ? 'bg-green-600/30 text-green-400 border border-green-500/50'
                        : team.payment_status === 'partial'
                        ? 'bg-blue-600/30 text-blue-400 border border-blue-500/50'
                        : 'bg-yellow-600/30 text-yellow-400 border border-yellow-500/50'
                    }`}>
                      {team.payment_status === 'completed' ? 'Registered' : team.payment_status === 'partial' ? 'Partial Payment' : 'Payment Pending'}
                    </span>
                  </div>
                  <p className="text-gray-400">Registration #{team.registration_number}</p>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Add Members button - always visible for registered teams */}
                  {isPaymentCompleted && team.total_members < 5 && (
                    <button
                      onClick={handleEditTeam}
                      className="px-4 py-2 bg-purple-600/30 text-purple-400 hover:bg-purple-600/50 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
                      title="Add more team members"
                      data-testid="add-members-btn"
                    >
                      <Plus size={18} />
                      Add Members
                    </button>
                  )}
                  
                  {/* Edit button - only before payment */}
                  {!isPaymentCompleted && (
                    <button
                      onClick={handleEditTeam}
                      className="p-2 text-gray-400 hover:bg-white/10 rounded-lg transition-colors"
                      title="Edit Team"
                      data-testid="edit-team-btn"
                    >
                      <Edit size={20} />
                    </button>
                  )}
                  
                  {/* Delete button - only before payment */}
                  {!isPaymentCompleted && (
                    <button
                      onClick={handleDeleteTeam}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Delete Team"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">AI Focus Area</p>
                  <p className="font-semibold text-purple-400">{team.ai_focus_area}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Team Size</p>
                  <p className="font-semibold">{team.total_members} Members</p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Fee</p>
                  <p className="font-semibold text-green-400">₹{team.total_price?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Project Idea */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Brain size={20} className="text-purple-400" />
                Project Idea
              </h3>
              <p className="text-gray-300 whitespace-pre-wrap">{team.project_idea}</p>
            </div>

            {/* Team Members */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users size={20} className="text-purple-400" />
                Team Members
              </h3>
              <div className="space-y-3">
                {team.members?.map((member, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                        {member.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold flex items-center gap-2 flex-wrap">
                          {member.name}
                          {member.is_leader && (
                            <span className="px-2 py-0.5 bg-purple-600/30 text-purple-400 rounded-full text-xs">Leader</span>
                          )}
                          {/* Show payment status badge per member */}
                          {member.payment_status === 'completed' ? (
                            <span className="px-2 py-0.5 bg-green-600/30 text-green-400 rounded-full text-xs flex items-center gap-1">
                              <CheckCircle size={12} /> Paid
                            </span>
                          ) : member.payment_status === 'pending' && isPaymentCompleted ? (
                            <span className="px-2 py-0.5 bg-yellow-600/30 text-yellow-400 rounded-full text-xs flex items-center gap-1">
                              <Clock size={12} /> Payment Pending
                            </span>
                          ) : null}
                        </p>
                        <p className="text-sm text-gray-400">{member.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">{member.college}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Action - show for pending or partial payment */}
            {(team.payment_status === 'pending' || team.payment_status === 'partial') && (
              <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 border border-purple-500/30 rounded-2xl p-6 text-center">
                <CreditCard className="text-purple-400 mx-auto mb-4" size={40} />
                <h3 className="text-xl font-bold mb-2">
                  {team.payment_status === 'partial' ? 'Complete Payment for New Members' : 'Complete Your Registration'}
                </h3>
                <p className="text-gray-400 mb-2">
                  {team.payment_status === 'partial' 
                    ? `Pay ₹${(team.amount_due || 0).toLocaleString()} for ${Math.round((team.amount_due || 0) / (config?.entry_fee || 3999))} new member(s)`
                    : `Pay ₹${(team.amount_due || team.total_price)?.toLocaleString()} to confirm your IAPL participation`
                  }
                </p>
                {team.payment_status === 'partial' && (
                  <p className="text-green-400 text-sm mb-4">
                    {team.paid_member_count || (team.total_members - Math.round((team.amount_due || 0) / (config?.entry_fee || 3999)))} member(s) already paid
                  </p>
                )}
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white px-8 py-3 rounded-lg font-medium transition-all"
                  data-testid="iapl-pay-btn"
                >
                  Pay Now
                </button>
              </div>
            )}
          </div>
        )}

        {/* Edit Team Form - for adding members or editing before payment */}
        {showEditForm && team && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">
                  {isPaymentCompleted ? 'Add Team Members' : 'Edit IAPL Team'}
                </h2>
                {isPaymentCompleted && (
                  <p className="text-gray-400 text-sm mt-1">
                    Add new members to your registered team. New members will require payment.
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowEditForm(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateTeam} className="space-y-6">
              {/* Team Info - disabled after payment */}
              {!isPaymentCompleted && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Team Name *</label>
                    <input
                      type="text"
                      value={formData.team_name}
                      onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="Enter team name"
                      required
                      minLength={3}
                      maxLength={50}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">AI Focus Area *</label>
                    <select
                      value={formData.ai_focus_area}
                      onChange={(e) => setFormData({ ...formData, ai_focus_area: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      required
                    >
                      <option value="">Select Focus Area</option>
                      {config.ai_focus_areas?.map((area, idx) => (
                        <option key={idx} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {!isPaymentCompleted && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Project Idea * <span className="text-gray-500">({formData.project_idea.length}/2000)</span>
                  </label>
                  <textarea
                    value={formData.project_idea}
                    onChange={(e) => setFormData({ ...formData, project_idea: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Describe your AI project idea in detail (minimum 50 characters)"
                    rows={4}
                    required
                    minLength={50}
                    maxLength={2000}
                  />
                </div>
              )}

              {/* Team Members */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-medium text-gray-300">
                    Team Members ({formData.members.length}/5) *
                  </label>
                  <button
                    type="button"
                    onClick={addMember}
                    disabled={formData.members.length >= 5}
                    className="text-purple-400 hover:text-purple-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Plus size={16} /> Add Member
                  </button>
                </div>

                <div className="space-y-4">
                  {formData.members.map((member, index) => {
                    const isPaidMember = member.payment_status === 'completed';
                    const isExistingPaidMember = isPaymentCompleted && isPaidMember;
                    
                    return (
                      <div key={index} className={`border rounded-lg p-4 ${isExistingPaidMember ? 'bg-green-900/10 border-green-500/30' : 'bg-white/5 border-white/10'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-purple-400 flex items-center gap-2">
                            {member.is_leader ? 'Team Leader' : `Member ${index + 1}`}
                            {isExistingPaidMember && (
                              <span className="px-2 py-0.5 bg-green-600/30 text-green-400 rounded-full text-xs flex items-center gap-1">
                                <CheckCircle size={12} /> Paid
                              </span>
                            )}
                            {!isPaidMember && isPaymentCompleted && (
                              <span className="px-2 py-0.5 bg-yellow-600/30 text-yellow-400 rounded-full text-xs">New - Payment Required</span>
                            )}
                          </span>
                          {/* Allow removing non-leader unpaid members or any member before payment */}
                          {!member.is_leader && (!isPaymentCompleted || !isPaidMember) && formData.members.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeMember(index)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) => updateMember(index, 'name', e.target.value)}
                            className={`px-3 py-2 border rounded-lg text-white text-sm ${isExistingPaidMember ? 'bg-green-900/20 border-green-500/30 cursor-not-allowed' : 'bg-white/5 border-white/20'}`}
                            placeholder="Full Name *"
                            required
                            disabled={isExistingPaidMember}
                          />
                          <input
                            type="email"
                            value={member.email}
                            onChange={(e) => updateMember(index, 'email', e.target.value)}
                            className={`px-3 py-2 border rounded-lg text-white text-sm ${isExistingPaidMember ? 'bg-green-900/20 border-green-500/30 cursor-not-allowed' : 'bg-white/5 border-white/20'}`}
                            placeholder="Email *"
                            required
                            disabled={isExistingPaidMember}
                          />
                          <input
                            type="tel"
                            value={member.phone}
                            onChange={(e) => updateMember(index, 'phone', e.target.value)}
                            className={`px-3 py-2 border rounded-lg text-white text-sm ${isExistingPaidMember ? 'bg-green-900/20 border-green-500/30 cursor-not-allowed' : 'bg-white/5 border-white/20'}`}
                            placeholder="Phone *"
                            required
                            disabled={isExistingPaidMember}
                          />
                          <input
                            type="text"
                            value={member.college}
                            onChange={(e) => updateMember(index, 'college', e.target.value)}
                            className={`px-3 py-2 border rounded-lg text-white text-sm ${isExistingPaidMember ? 'bg-green-900/20 border-green-500/30 cursor-not-allowed' : 'bg-white/5 border-white/20'}`}
                            placeholder="College/University *"
                            required
                            disabled={isExistingPaidMember}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {formData.members.length < 2 && (
                  <p className="text-yellow-400 text-sm mt-2 flex items-center gap-2">
                    <AlertCircle size={16} />
                    IAPL teams require at least 2 members
                  </p>
                )}
              </div>

              {/* Pricing Summary for Edit */}
              <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
                {isPaymentCompleted ? (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Already Paid Members</span>
                      <span className="text-green-400 font-medium">
                        {formData.members.filter(m => m.payment_status === 'completed').length} members
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">New Members Fee ({formData.members.filter(m => m.payment_status !== 'completed').length} × ₹{(config?.entry_fee || 3999).toLocaleString()})</span>
                      <span className="text-2xl font-bold text-white">
                        ₹{((config?.entry_fee || 3999) * formData.members.filter(m => m.payment_status !== 'completed').length).toLocaleString()}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Entry Fee ({formData.members.length} members × ₹{(config?.entry_fee || 3999).toLocaleString()})</span>
                    <span className="text-2xl font-bold text-white">
                      ₹{((config?.entry_fee || 3999) * formData.members.length).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowEditForm(false)}
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || formData.members.length < 2}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  data-testid="update-iapl-team-btn"
                >
                  {submitting ? 'Saving...' : isPaymentCompleted ? 'Add Members & Update Team' : 'Update Team'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && team && (
          <CashfreeCheckout
            team={{
              ...team,
              // Map IAPL team structure to CashfreeCheckout expected format
              total_price: team.total_price || team.amount_due,
              amount_due: team.amount_due || team.total_price,
            }}
            teamId={team.id}
            paymentType="iapl_team_registration"
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
            onClose={() => setShowPaymentModal(false)}
            allowCoupon={false}
            feeType="iapl_entry_fee"
            buttonText="Pay Now"
            buttonClassName="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white"
          />
        )}
      </div>
    </div>
  );
};

export default IAPLDashboardPage;
